import React from 'react';
import ReactDOM from 'react-dom';

interface FullscreenMediaPopupProps {
  isOpen: boolean;
  src?: string;
  mediaType?: 'image' | 'video';
  onClose: () => void;
  closeLabel?: string;
  forceLandscape?: boolean;
  videoInitialTime?: number;
  videoInitialPaused?: boolean;
  onVideoStateChange?: (state: { currentTime: number; paused: boolean }) => void;
  disableVideoControls?: boolean;
  closeOnVideoEnd?: boolean;
}

function FullscreenMediaPopup({
  isOpen,
  src,
  mediaType,
  onClose,
  closeLabel = 'Zamknij',
  forceLandscape = false,
  videoInitialTime = 0,
  videoInitialPaused = false,
  onVideoStateChange,
  disableVideoControls = false,
  closeOnVideoEnd = false,
}: FullscreenMediaPopupProps) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  React.useEffect(() => {
    if (mediaType !== 'video' || !videoRef.current) {
      return;
    }

    const video = videoRef.current;
    video.preload = 'auto';
    video.load();
  }, [src, mediaType]);

  React.useEffect(() => {
    if (!isOpen || mediaType !== 'video' || !videoRef.current) {
      return;
    }

    const video = videoRef.current;

    const applyInitialState = async () => {
      try {
        video.currentTime = videoInitialTime || 0;
      } catch (error) {
        // Ignore seek errors for very short/unready media.
      }

      if (videoInitialPaused) {
        video.pause();
      } else {
        try {
          await video.play();
        } catch (error) {
          // Autoplay may be blocked by the browser.
        }
      }

      onVideoStateChange?.({ currentTime: video.currentTime, paused: video.paused });
    };

    applyInitialState();
  }, [isOpen, mediaType, videoInitialTime, videoInitialPaused, onVideoStateChange]);

  const emitVideoState = React.useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    onVideoStateChange?.({
      currentTime: videoRef.current.currentTime,
      paused: videoRef.current.paused,
    });
  }, [onVideoStateChange]);

  if (!src || !mediaType) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={`fullscreen-media-popup ${isOpen ? 'fullscreen-media-popup--open' : ''} ${forceLandscape ? 'fullscreen-media-popup--force-landscape' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Podglad multimedia">
      <div className="fullscreen-media-popup__overlay" onClick={onClose} />
      <div className="fullscreen-media-popup__content">
        <button type="button" className="fullscreen-media-popup__close" onClick={onClose}>
          {closeLabel}
        </button>
        <div className="fullscreen-media-popup__media-wrapper">
          {mediaType === 'video' ? (
            <video
              ref={videoRef}
              className={`fullscreen-media-popup__media ${disableVideoControls ? 'fullscreen-media-popup__media--locked' : ''}`}
              src={src}
              controls={!disableVideoControls}
              autoPlay={false}
              muted
              playsInline
              preload="auto"
              onTimeUpdate={emitVideoState}
              onPlay={emitVideoState}
              onPause={emitVideoState}
              onSeeked={emitVideoState}
              onLoadedData={emitVideoState}
              onEnded={() => {
                emitVideoState();
                if (closeOnVideoEnd) {
                  onClose();
                }
              }}>
              Twoja przegladarka nie obsluguje elementu video.
            </video>
          ) : (
            <img className="fullscreen-media-popup__media" src={src} alt="" />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default FullscreenMediaPopup;
